import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { readSignupIntent } from '../../../onboarding/signupIntent';
import { authIntentCopy, authV2Messages } from './authV2Messages';
import { renderGradient } from './authRichText';
import { AuthTrialLine } from './AuthTrialLine';
import { withBrandWord } from '../../../music24/components/primitives/BrandWord';

export type AuthMode = 'signup' | 'signin';

interface AuthEmailHeadingProps {
  mode: AuthMode;
  /** Sign-in was opened by a team invite link (`returnTo=team/accept`). */
  isInvite: boolean;
  /** The workspace and the inviter of the invite, when the invite link carries them. */
  workspaceName: string | null;
  inviterName: string | null;
}

interface HeadingCopy {
  eyebrow: string;
  headline: React.ReactNode;
  lead: string;
  showTrial: boolean;
}

/**
 * The copy block over the email field.
 * design: onboarding g1 (signup, no intent), g2-g5 (the headline by intent), g7 (invite), g10 (sign in).
 * The intent was kept by `captureSignupIntent` before this screen rendered.
 */
const AuthEmailHeadingComponent: React.FC<AuthEmailHeadingProps> = ({ mode, isInvite, workspaceName, inviterName }) => {
  const intl = useIntl();

  const copy = useMemo((): HeadingCopy => {
    if (isInvite) {
      return {
        eyebrow: intl.formatMessage(authV2Messages.inviteEyebrow),
        headline:
          workspaceName !== null
            ? intl.formatMessage(authV2Messages.inviteTitle, { workspace: workspaceName, grad: renderGradient })
            : intl.formatMessage(authV2Messages.inviteTitleNoName, { grad: renderGradient }),
        lead:
          inviterName !== null
            ? intl.formatMessage(authV2Messages.inviteLead, { inviter: inviterName })
            : intl.formatMessage(authV2Messages.inviteLeadNoName),
        showTrial: false,
      };
    }
    if (mode === 'signin') {
      return {
        eyebrow: intl.formatMessage(authV2Messages.signinEyebrow),
        headline: intl.formatMessage(authV2Messages.signinHeadline, { grad: renderGradient }),
        lead: intl.formatMessage(authV2Messages.signinLead),
        showTrial: false,
      };
    }
    const intentCopy = authIntentCopy[readSignupIntent() ?? 'default'];
    return {
      eyebrow: intl.formatMessage(authV2Messages.eyebrowSignup),
      headline: intl.formatMessage(intentCopy.headline, { grad: renderGradient }),
      lead: intl.formatMessage(intentCopy.lead),
      showTrial: true,
    };
  }, [intl, isInvite, mode, workspaceName, inviterName]);

  return (
    <div className="au-head">
      <span className="au-eyebrow">
        <span className="dot" />
        {withBrandWord(copy.eyebrow)}
      </span>
      <h1 className="au-h">{copy.headline}</h1>
      <p className="au-lead">{withBrandWord(copy.lead)}</p>
      {copy.showTrial ? <AuthTrialLine /> : null}
    </div>
  );
};

export const AuthEmailHeading = TypedMemo(AuthEmailHeadingComponent);
